import { useEffect, useEffectEvent } from "react";

/**
 * 스크롤·크기 바뀜에 반응하는 사이트 일(머리말 그림자·구역 눈금·장 바로가기·패럴랙스)을 프레임당 한 번으로 모은다.
 * 구독자가 여럿이어도 스크롤 리스너와 rAF 는 하나다. 콜백 안에서는 레이아웃 읽기를 한 번에 모아 하고 DOM 에 직접 쓴다
 * (이동 빈도 값은 React state 에 두지 않는다 — code-quality-guide).
 */
export type ScrollFrameCallback = (
  scrollY: number,
  viewportHeight: number
) => void;

const callbacks = new Set<ScrollFrameCallback>();
let frameRequested = false;

const flush = () => {
  frameRequested = false;
  const scrollY = window.scrollY;
  const viewportHeight = window.innerHeight;
  callbacks.forEach((callback) => callback(scrollY, viewportHeight));
};

const requestFlush = () => {
  if (frameRequested) return;
  frameRequested = true;
  requestAnimationFrame(flush);
};

export function subscribeScrollFrame(
  callback: ScrollFrameCallback
): () => void {
  if (callbacks.size === 0) {
    window.addEventListener("scroll", requestFlush, { passive: true });
    window.addEventListener("resize", requestFlush, { passive: true });
  }
  callbacks.add(callback);
  requestFlush();
  return () => {
    callbacks.delete(callback);
    if (callbacks.size === 0) {
      window.removeEventListener("scroll", requestFlush);
      window.removeEventListener("resize", requestFlush);
    }
  };
}

/** 마운트 동안 스크롤 프레임마다 `callback` 을 부른다. 콜백은 늘 최신 렌더의 것을 쓴다(구독은 다시 하지 않는다). */
export function useScrollFrame(callback: ScrollFrameCallback, enabled = true) {
  const onFrame = useEffectEvent(callback);
  useEffect(() => {
    if (!enabled) return undefined;
    return subscribeScrollFrame((scrollY, viewportHeight) =>
      onFrame(scrollY, viewportHeight)
    );
  }, [enabled]);
}
